import React, { useEffect, useState, useRef } from 'react';
import { 
  FileText, 
  Target, 
  Shield, 
  Coins, 
  Users, 
  TrendingUp, 
  Zap, 
  Clock,
  Layers,
  CheckCircle,
  Gift,
  ArrowUp,
  Sparkles,
  BarChart3,
  Globe,
  Copy,
  ExternalLink,
  Wallet,
  Building2
} from 'lucide-react';
import './Whitepaper.css';

const Whitepaper = () => {
  const [progress, setProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(scrolled);
      setShowBackToTop(scrollTop > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const copyToClipboard = async (address, contractName) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(contractName);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="whitepaper-page">
      {/* Progress Bar */}
      <div className="scroll-progress" style={{ width: `${progress}%` }} />

      {/* Header */}
      <header className="whitepaper-header">
        <div className="header-badge">
          <FileText size={24} />
          <span>TRENDPHILE White Paper</span>
        </div>
        <h1>The Future of Social Prediction Markets</h1>
        <p className="subtitle">
          Rewarding quality insights with TPHILE tokens, creating the first truly equitable prediction ecosystem
        </p>
        <div className="header-divider"></div>
      </header>

      {/* Table of Contents (Desktop) */}
      <aside className="toc-sidebar">
        <h3>Contents</h3>
        <nav className="toc-nav">
          <a href="#executive-summary" onClick={(e) => { e.preventDefault(); scrollToSection('executive-summary'); }}>Executive Summary</a>
          <a href="#problem" onClick={(e) => { e.preventDefault(); scrollToSection('problem'); }}>The Problem</a>
          <a href="#solution" onClick={(e) => { e.preventDefault(); scrollToSection('solution'); }}>The Solution</a>
          <a href="#why-now" onClick={(e) => { e.preventDefault(); scrollToSection('why-now'); }}>Why This Matters Now</a>
          <a href="#revenue" onClick={(e) => { e.preventDefault(); scrollToSection('revenue'); }}>Revenue Model</a>
          <a href="#token" onClick={(e) => { e.preventDefault(); scrollToSection('token'); }}>The TPH Token</a>
          <a href="#smart-contracts" onClick={(e) => { e.preventDefault(); scrollToSection('smart-contracts'); }}>Smart Contracts</a>
          <a href="#technology" onClick={(e) => { e.preventDefault(); scrollToSection('technology'); }}>Technology & Security</a>
          <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }}>Team & Vision</a>
          <a href="#growth" onClick={(e) => { e.preventDefault(); scrollToSection('growth'); }}>Growth Strategy</a>
          <a href="#conclusion" onClick={(e) => { e.preventDefault(); scrollToSection('conclusion'); }}>Conclusion</a>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="whitepaper-content">
        {/* Executive Summary */}
        <section id="executive-summary" className="wp-section">
          <div className="section-header">
            <Sparkles className="section-icon" />
            <h2>Executive Summary</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile is a revolutionary prediction platform that recognizes a fundamental truth: people's predictions and expectations are valuable data, yet they've never been fairly compensated for sharing them. While traditional platforms extract value from user insights without reciprocation, Trendphile flips this model by rewarding quality predictions with TPH tokens, creating the first truly equitable prediction ecosystem.
            </p>
            <p>
              Our mission is simple but powerful: spot and detect trends before they go mainstream by incentivizing users to share their genuine expectations in real-time, then reward them fairly for quality contributions. Through advanced AI-powered fraud detection, multi-factor verification, and quality-gated rewards, we ensure that only authentic, high-quality predictions earn rewards, creating a trusted data source that benefits everyone from traders and consultants to governments and researchers.
            </p>
          </div>
        </section>

        {/* The Problem */}
        <section id="problem" className="wp-section">
          <div className="section-header">
            <Target className="section-icon" />
            <h2>The Problem: Valuable Insights, Unfair Compensation</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Every day, millions of people form predictions about politics, markets, technology, and culture. These insights represent enormous value when aggregated and analyzed correctly. Yet today's landscape offers no fair mechanism to compensate people for sharing their genuine expectations.
            </p>
            <p>
              Traditional prediction markets suffer from several critical flaws. They're dominated by spam, bots, and low-quality content because there's no real incentive to maintain standards. Users submit predictions but receive nothing in return, while platform operators extract all the value. There's no quality control mechanism, no fraud prevention, and no way to separate genuine human insight from AI-generated noise.
            </p>
            <p>
              This creates a lose-lose situation: users give away valuable data for free, while platforms struggle with data quality so poor it's nearly worthless for serious analysis. The market desperately needs a solution that fairly compensates quality contributions while maintaining high standards through automated verification.
            </p>
          </div>
        </section>

        {/* The Solution */}
        <section id="solution" className="wp-section">
          <div className="section-header">
            <Shield className="section-icon" />
            <h2>The Trendphile Solution</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile solves this problem through a sophisticated combination of anti-fraud technology and token-based incentives. When users submit predictions, our platform analyzes them in real-time using OpenAI integration, rule-based validation, device fingerprinting, and multi-factor authentication through Google OAuth and hCaptcha.
            </p>
            <p>
              Every prediction receives a quality score. Only submissions scoring above seventy percent qualify for rewards. This quality gate ensures our dataset remains valuable while creating a clear incentive: craft thoughtful, genuine predictions and get rewarded. Submit spam or low-effort content and get nothing.
            </p>
            
            <div className="subsection">
              <h3><Zap className="subsection-icon" /> The Dynamic Reward Mechanism</h3>
              <p>
                Trendphile implements a sophisticated four-year emission schedule designed to bootstrap early growth while ensuring long-term token sustainability. The reward formula is elegantly simple yet powerful: quality predictions earn a base rate multiplied by the quality score percentage.
              </p>
              <div className="reward-schedule">
                <div className="reward-year">
                  <h4>Year 1 (500 TPH base rate)</h4>
                  <p>A prediction scoring 85% earns <strong>425 TPH tokens</strong> (500 × 0.85). This aggressive first-year rate incentivizes early adopters and rapidly builds our initial community.</p>
                </div>
                <div className="reward-year">
                  <h4>Year 2 (250 TPH base rate)</h4>
                  <p>The same 85% quality prediction earns <strong>212.5 TPH tokens</strong> (250 × 0.85). This reduction maintains engagement while controlling token inflation as our user base expands.</p>
                </div>
                <div className="reward-year">
                  <h4>Year 3 (150 TPH base rate)</h4>
                  <p>An 85% prediction earns <strong>127.5 TPH tokens</strong> (150 × 0.85). By year three, we prioritize quality over quantity, rewarding sustained high-quality contributions.</p>
                </div>
                <div className="reward-year">
                  <h4>Year 4 (50 TPH base rate)</h4>
                  <p>The same prediction earns <strong>42.5 TPH tokens</strong> (50 × 0.85). This final reduction establishes our long-term sustainable reward economy.</p>
                </div>
              </div>
              <p>
                This progressive rate decrease creates powerful early-adopter advantages while ensuring token longevity. Total emission across four years: <strong>500 million TPH</strong> (150M in years 1-3, 50M in year 4), representing a <strong>90% rate reduction</strong> from year one to year four.
              </p>
              <p>
                Beyond prediction rewards, users earn through referrals bringing <strong>ten thousand TPH per new verified user</strong> who submits predictions, and social sharing generating <strong>one hundred TPH daily</strong> (maximum five shares). These mechanisms accelerate growth while maintaining quality standards through our comprehensive verification systems.
              </p>
            </div>
          </div>
        </section>

        {/* Why This Matters Now */}
        <section id="why-now" className="wp-section">
          <div className="section-header">
            <Clock className="section-icon" />
            <h2>Why This Matters Now</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              The timing for Trendphile couldn't be better. We're at a unique intersection of technological capability and market need. AI has finally reached the sophistication to perform real-time quality analysis at scale. Blockchain technology enables instant, transparent token distribution. And most critically, there's growing recognition that user-generated data has real value that should flow back to creators.
            </p>
            <p>
              Traditional data markets are broken. Users create value but capture none of it. Trendphile represents the future: a platform where your insights directly generate rewards, where quality is objectively measured and fairly compensated, and where everyone benefits from the collective intelligence of the crowd.
            </p>
            <p>
              Our ability to detect trends before they go mainstream creates value for multiple stakeholders. Traders get early signals on market sentiment. Consultants access real-time data on emerging trends. Governments tap into genuine public opinion. Researchers obtain quality prediction data for academic study. And users get fairly compensated for sharing their genuine expectations.
            </p>
          </div>
        </section>

        {/* Revenue Model */}
        <section id="revenue" className="wp-section">
          <div className="section-header">
            <BarChart3 className="section-icon" />
            <h2>Revenue Model</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile will generate sustainable revenue through advanced analytics. This model aligns perfectly with our dual-market approach. Individual users earn tokens by contributing quality predictions at no cost. Professional users pay for the premium analytical tools that transform raw prediction data into actionable intelligence. Revenue flows back into platform development, security audits, and community growth initiatives.
            </p>
          </div>
        </section>

        {/* The TPH Token */}
        <section id="token" className="wp-section">
          <div className="section-header">
            <Coins className="section-icon" />
            <h2>The TPH Token</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              The Trendphile token (TPH) serves as the ecosystem's reward and utility token with a maximum supply of <strong>one billion tokens</strong>, structured to support long-term sustainable growth.
            </p>

            <div className="subsection">
              <h3>Token Distribution</h3>
              <div className="token-distribution">
                <div className="dist-item">
                  <h4>500M TPH (50%) - User Rewards</h4>
                  <p>Distributed over four years through our controlled emission schedule. This massive allocation ensures we can reward quality contributions sustainably while maintaining token value through progressive rate decreases.</p>
                </div>
                <div className="dist-item">
                  <h4>200M TPH (20%) - Liquidity</h4>
                  <p>Essential for maintaining healthy token markets and enabling users to convert their earned rewards. Our liquidity strategy launches conservatively with thirty million tokens at TGE on Polygon's QuickSwap DEX. The remaining one hundred seventy million tokens will be deployed gradually across multiple chains and liquidity pools as our ecosystem expands.</p>
                </div>
                <div className="dist-item">
                  <h4>150M TPH (15%) - Team</h4>
                  <p>Vesting over twenty-four months total with a six-month cliff. This breaks down to six months of complete lockup, followed by eighteen months of linear vesting. The allocation is split equally between two team members (seventy-five million tokens each), aligning team incentives with long-term platform success.</p>
                </div>
                <div className="dist-item">
                  <h4>150M TPH (15%) - Treasury</h4>
                  <p>Managed initially by the team to finance partnerships, marketing, security audits, and platform development. The treasury allocation is structured as follows: ninety million TPH for profile completion rewards, ten million TPH for marketing initiatives, and fifty million TPH as an emergency reserve.</p>
                </div>
              </div>
            </div>

            <div className="subsection">
              <h3>4-Year Emission Schedule</h3>
              <ul className="emission-list">
                <li><strong>Year 1:</strong> 150 million TPH (Base rate: 500 TPH per prediction)</li>
                <li><strong>Year 2:</strong> 150 million TPH (Base rate: 250 TPH per prediction)</li>
                <li><strong>Year 3:</strong> 150 million TPH (Base rate: 150 TPH per prediction)</li>
                <li><strong>Year 4:</strong> 50 million TPH (Base rate: 50 TPH per prediction)</li>
              </ul>
              <p>
                This deflationary emission model represents a <strong>90% rate reduction</strong> from year one to year four, creating powerful early-adopter incentives while ensuring long-term sustainability. Rewards split approximately 70% for predictions, 20% for referrals, and 10% for social sharing.
              </p>
            </div>

            <div className="subsection">
              <h3>Token Launch Strategy</h3>
              <p>
                Trendphile's Token Generation Event occurs when we achieve two critical milestones: <strong>ten thousand verified users</strong> who have submitted <strong>one hundred thousand predictions</strong>. This ensures we've proven market fit and built genuine community engagement before launching our token.
              </p>
              <p>
                At TGE, we immediately unlock the first year's emission allocation (150M TPH) for earned distributions, thirty million tokens for initial liquidity on Polygon's QuickSwap, and necessary treasury allocations. Team tokens remain fully locked for six months, then vest linearly over the following eighteen months (total vesting period: twenty-four months from TGE).
              </p>
            </div>

            <div className="subsection">
              <h3>Multi-Chain Liquidity Expansion</h3>
              <p>
                TPH launches on Polygon but won't stay there exclusively. As trading volume and community grow, we'll strategically deploy the remaining one hundred seventy million liquidity tokens across:
              </p>
              <ul>
                <li>Additional Polygon DEXs (SushiSwap, Uniswap V3)</li>
                <li>Ethereum mainnet (for institutional access)</li>
                <li>Other EVM-compatible chains (Arbitrum, Optimism, Base)</li>
                <li>Centralized exchange listings (as volume justifies)</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>Token Utility</h3>
              <p>
                TPH holders unlock multiple benefits beyond earning rewards. Governance rights enable voting on platform decisions, giving the community real influence over Trendphile's evolution. Premium feature access requires holding specific TPH amounts, creating organic demand. Exclusive predictions from top analysts become available only to TPH holders. Community status directly correlates with holdings, creating social capital within our ecosystem.
              </p>
              <p>
                This multi-faceted utility ensures TPH isn't just a reward token but a functional asset with diverse use cases driving sustained demand.
              </p>
            </div>

            <div className="subsection">
              <h3>Deflationary Mechanisms</h3>
              <p>
                To maintain long-term token value, our team commits to strategic token burning. Rather than implementing automatic burn mechanisms that might create uncertainty, we'll execute periodic team-initiated burns funded from platform revenue and strategic reserves.
              </p>
              <p>
                These transparent, announced burns build trust while gradually reducing supply. The burning capability coded into our smart contract ensures technical readiness while our discretionary approach allows optimization based on market conditions and community feedback. Combined with our decreasing emission schedule, this creates a powerful deflationary economic model focused on value preservation.
              </p>
            </div>
          </div>
        </section>

        {/* Smart Contracts */}
        <section id="smart-contracts" className="wp-section">
          <div className="section-header">
            <Layers className="section-icon" />
            <h2>Smart Contracts</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              All Tphile smart contracts are deployed on Polygon (MATIC) blockchain and are fully verified on PolygonScan. The source code is open source and publicly auditable, ensuring complete transparency and security.
            </p>

            <div className="contracts-grid">
              {/* TPHILE Token Contract */}
              <div className="contract-card">
                <div className="contract-header">
                  <div className="contract-title-group">
                    <Coins className="contract-icon" />
                    <div>
                      <h3 className="contract-name">
                        TPHILE Token Contract
                        <span className="verified-badge">
                          <CheckCircle size={14} />
                          Verified
                        </span>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="contract-address-wrapper">
                  <code className="contract-address">0x9A77152343981490c6477aF75A7c3FCA28cf9f00</code>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard('0x9A77152343981490c6477aF75A7c3FCA28cf9f00', 'token')}
                    aria-label="Copy address"
                  >
                    {copiedAddress === 'token' ? (
                      <>
                        <CheckCircle size={14} />
                        Copied ✓
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="contract-description">Main token contract for TPhile ecosystem</p>
                <a
                  href="https://polygonscan.com/address/0x9A77152343981490c6477aF75A7c3FCA28cf9f00"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-button"
                >
                  <ExternalLink size={16} />
                  View on PolygonScan
                </a>
              </div>

              {/* Team Vesting Contract */}
              <div className="contract-card">
                <div className="contract-header">
                  <div className="contract-title-group">
                    <Users className="contract-icon" />
                    <div>
                      <h3 className="contract-name">
                        Team Vesting Contract
                        <span className="verified-badge">
                          <CheckCircle size={14} />
                          Verified
                        </span>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="contract-address-wrapper">
                  <code className="contract-address">0xA5b953726a8216Cf343903c4493216a0Af99e4f5</code>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard('0xA5b953726a8216Cf343903c4493216a0Af99e4f5', 'team')}
                    aria-label="Copy address"
                  >
                    {copiedAddress === 'team' ? (
                      <>
                        <CheckCircle size={14} />
                        Copied ✓
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="contract-description">Manages 24-month team token vesting starting at TGE</p>
                <a
                  href="https://polygonscan.com/address/0xA5b953726a8216Cf343903c4493216a0Af99e4f5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-button"
                >
                  <ExternalLink size={16} />
                  View on PolygonScan
                </a>
              </div>

              {/* Treasury Manager Contract */}
              <div className="contract-card">
                <div className="contract-header">
                  <div className="contract-title-group">
                    <Building2 className="contract-icon" />
                    <div>
                      <h3 className="contract-name">
                        Treasury Manager Contract
                        <span className="verified-badge">
                          <CheckCircle size={14} />
                          Verified
                        </span>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="contract-address-wrapper">
                  <code className="contract-address">0xa2014ad6d62dd9fe57531f6e97544bfea2674192</code>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard('0xa2014ad6d62dd9fe57531f6e97544bfea2674192', 'treasury')}
                    aria-label="Copy address"
                  >
                    {copiedAddress === 'treasury' ? (
                      <>
                        <CheckCircle size={14} />
                        Copied ✓
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="contract-description">Manages treasury operations and fund distribution</p>
                <a
                  href="https://polygonscan.com/address/0xa2014ad6d62dd9fe57531f6e97544bfea2674192"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-button"
                >
                  <ExternalLink size={16} />
                  View on PolygonScan
                </a>
              </div>

              {/* User Vesting Contract */}
              <div className="contract-card">
                <div className="contract-header">
                  <div className="contract-title-group">
                    <Gift className="contract-icon" />
                    <div>
                      <h3 className="contract-name">
                        User Vesting Contract
                        <span className="verified-badge">
                          <CheckCircle size={14} />
                          Verified
                        </span>
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="contract-address-wrapper">
                  <code className="contract-address">0x14255b60dEbC4306A3fbF31CFBba182B07a1715f</code>
                  <button
                    className="copy-button"
                    onClick={() => copyToClipboard('0x14255b60dEbC4306A3fbF31CFBba182B07a1715f', 'user')}
                    aria-label="Copy address"
                  >
                    {copiedAddress === 'user' ? (
                      <>
                        <CheckCircle size={14} />
                        Copied ✓
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="contract-description">User rewards: 50% unlocked at TGE, remaining 50% at 6 months</p>
                <a
                  href="https://polygonscan.com/address/0x14255b60dEbC4306A3fbF31CFBba182B07a1715f"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-button"
                >
                  <ExternalLink size={16} />
                  View on PolygonScan
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Technology and Security */}
        <section id="technology" className="wp-section">
          <div className="section-header">
            <Shield className="section-icon" />
            <h2>Technology and Security</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile's technical infrastructure prioritizes security, transparency, and reliability. All smart contracts are deployed on <strong>Polygon (MATIC)</strong>, chosen for its low fees, high throughput, and robust ecosystem support. Our contracts are written in <strong>Solidity 0.8.28</strong>, leveraging OpenZeppelin's battle-tested libraries for security-critical functions.
            </p>
            <p>
              Our token contract implements comprehensive role-based access control with distinct ADMIN, MINTER, and PAUSER roles. Emergency functions enable quick response to threats while protecting user funds. The contract is <strong>immutable by design</strong>—no proxy patterns or upgradability—ensuring users can trust that rules won't change arbitrarily.
            </p>
            <p>
              Before mainnet deployment, all contracts undergo professional security audits by <strong>OpenZeppelin</strong>, industry leaders in smart contract security. This third-party verification ensures our code meets institutional-grade security standards.
            </p>
            <p>
              The platform's anti-fraud system combines multiple validation layers. OpenAI integration detects AI-generated content, rule-based validation catches obvious spam patterns, device fingerprinting prevents multi-account abuse, IP tracking identifies suspicious activity patterns, and Google OAuth plus hCaptcha verify human users. This defense-in-depth approach makes fraud economically unfeasible while remaining seamless for legitimate users.
            </p>
          </div>
        </section>

        {/* Team and Vision */}
        <section id="team" className="wp-section">
          <div className="section-header">
            <Users className="section-icon" />
            <h2>Team and Vision</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile is built by a two-person team operating from the <strong>United Arab Emirates</strong>, combining technical expertise with business acumen. This lean structure enables rapid iteration while our strategic location provides access to both Middle Eastern and global markets.
            </p>
            <p>
              Our governance model starts with owner-controlled decision making during the critical early phase. As we scale and decentralize, we'll progressively incorporate community input through structured governance proposals. This pragmatic approach balances speed and flexibility during early growth with the decentralized ethos of Web3.
            </p>
          </div>
        </section>

        {/* Growth Strategy */}
        <section id="growth" className="wp-section">
          <div className="section-header">
            <TrendingUp className="section-icon" />
            <h2>Growth Strategy</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile's user acquisition focuses on organic growth through crypto-native channels. Our primary strategy leverages Crypto Twitter and X for influencer marketing, connecting with opinion leaders who appreciate quality prediction data. Reddit communities in r/cryptocurrency and r/predictions provide natural homes for early adopters. Discord and Telegram community building creates sticky engagement and word-of-mouth growth.
            </p>
            <p>
              Our referral program, rewarding ten thousand TPH per successful verified user who submits predictions, supercharges organic growth by turning every satisfied user into a growth channel. This mechanism, combined with our aggressive Year 1 rewards, creates powerful viral mechanics.
            </p>
            <p>
              Rather than expensive paid advertising, we focus on demonstrating value through real product quality. Users who earn meaningful rewards naturally share their experience, creating authentic marketing far more effective than paid ads.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section id="conclusion" className="wp-section">
          <div className="section-header">
            <FileText className="section-icon" />
            <h2>Conclusion</h2>
          </div>
          <div className="section-divider"></div>
          <div className="section-content">
            <p>
              Trendphile represents a fundamental shift in how we think about prediction platforms and data value. By creating the first truly equitable system where quality insights receive fair compensation through a controlled, deflationary emission schedule, we're not just building another crypto project. We're establishing infrastructure for a new economy where your thoughts about the future directly generate value.
            </p>
            <p>
              Our combination of sophisticated fraud prevention, quality-gated rewards, four-year emission schedule, and multi-faceted token utility creates a sustainable ecosystem that benefits all participants. Users earn fair compensation for quality contributions with powerful early-adopter advantages. Professional consumers access trusted, high-quality prediction data. Token holders participate in a growing ecosystem with multiple utility dimensions and deflationary economics.
            </p>
            <p>
              The path forward is clear: build community through genuine value, launch tokens when we've proven market fit with ten thousand verified predictors submitting one hundred thousand predictions, scale through premium features that serve professional users, and progressively decentralize governance as our ecosystem matures. Every prediction, every reward, every validation builds toward our vision of democratized, fairly compensated collective intelligence.
            </p>
            <p className="conclusion-highlight">
              <strong>Trendphile isn't just predicting the future. We're building it—one quality prediction at a time, rewarding early adopters generously while ensuring long-term sustainability through our controlled emission strategy.</strong>
            </p>
            <div className="call-to-action">
              <h3>For More Information:</h3>
              <p>
                Join our community, submit your first prediction, and start earning TPH tokens for quality insights. The future of prediction platforms is here, and it rewards thinking—especially if you think early.
              </p>
            </div>
            <div className="disclaimer">
              <p>
                <em>This white paper presents Trendphile's current vision and plans as of November 2025. Plans may evolve based on community feedback, market conditions, and technical developments. All forward-looking statements are subject to risks and uncertainties.</em>
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button 
          className="back-to-top" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Whitepaper;
